import { maxBy, orderBy } from 'lodash';
import { FC, memo, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import FullScreenButton from '@/components/FullScreenButton';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import {
  ActionsWrap,
  SortableTh,
  Table,
  TableContent,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import OptimisationFinalValueTh from '@/pages/OptimisationOverview/components/OptimisationFinalValueTh';
import {
  OptimisationTestUnitResource,
  TestsSortState,
} from '@/types/resources';
import {
  GaNetworkType,
  OptimisationProjectNetworkEnum,
  OptimisationTestTypeEnum,
  SortDirection,
} from '@/utils/enums';
import { RootState } from '@/utils/store';

import { GaNetworkTypeContext } from '../index';
import AvgStatsRow from './AvgStatsRow';
import TestTableRow, { Dot } from './TestTableRow';
import TestsBatchFilter from './TestsBatchFilter';

const columns = [
  {
    title: 'Variation Name',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Views',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Video Title',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Thumb',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Video Duration',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'CTR',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Watchtime (s)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e30s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e45s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e60s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e80s Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e120s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e150s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e4min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e8min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e15min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e20min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e30min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e60min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '30s Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '8min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '25% Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '50% Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '75% Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '100% Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Final Value',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'eYTV',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'eYTV 30s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 45s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 60s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 80s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 120s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 150s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 4min',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'eYTV 15min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 20min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 30min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 60min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Channel',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Audience Channels',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Audience Segment',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'GA Campaign Status',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Max CPV',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Campaign Budget',
    toggable: true,
    defaultVisibility: false,
  },
];

const sequencesColumns = [
  ...columns,
  // {
  //   title: "Drop Visualization",
  //   toggable: true,
  //   defaultVisibility: false,
  // },
  // {
  //   title: "Drop",
  //   toggable: true,
  //   defaultVisibility: false,
  // },
];

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    vertical-align: top;
    white-space: nowrap;
    &:nth-child(1) {
      min-width: 280px;
      padding-left: 24px;
      white-space: initial;
    }
    &:last-child {
      min-width: 44px;
      padding-right: 16px;
    }
  }
`;

const ControlsHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const StyledViewOptionsModal = styled(ViewOptionsModal)`
  border-color: ${({ theme }) => theme.colors.lightGray};
`;

export const ResultsDescription = styled.div`
  margin-right: auto;
  margin-top: auto;
  display: flex;
  align-items: center;
  ${Text} {
    margin-right: 24px;
  }
  ${Dot} {
    margin-right: 8px;
  }
`;

export const ResultsDescriptionSvg = styled.svg`
  margin-right: 8px;
  path {
    fill: ${({ theme }) => theme.colors.white};
  }

  circle {
    fill: ${({ theme }) => theme.colors.gray};
  }
`;

const GaNetworkSwitch = styled.div`
  margin-right: 8px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 4px;
  display: flex;
  gap: 4px;
`;

const GaNetworkButton = styled(Text)<{ $active: boolean }>`
  border-radius: 4px;
  border: 1px solid transparent;
  color: ${({ theme }) => theme.colors.gray};
  width: 62px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }

  ${({ theme, $active }) =>
    $active &&
    css`
      border-color: ${theme.colors.lightGray};
      color: ${({ theme }) => theme.colors.black};
      background: #f2f2f2;
    `}
`;

interface Props {
  tests?: OptimisationTestUnitResource[];
  type: OptimisationTestTypeEnum;
  network: OptimisationProjectNetworkEnum;
  testParameter:
    | 'ctrPercentage'
    | 'milestoneViewsPercentage'
    | 'avgWatchtime'
    | 'estimatedFinalValue';
}

const TestTable: FC<Props> = memo(({ tests, type, testParameter, network }) => {
  const optionsDataKey = () => {
    switch (type) {
      case OptimisationTestTypeEnum.THUMBS:
        return 'optimisationThumbsTable';
      case OptimisationTestTypeEnum.TIPS:
        return 'optimisationTipsTable';
      case OptimisationTestTypeEnum.SEQUENCES:
        return 'optimisationSequenceTable';
      case OptimisationTestTypeEnum.INTRO:
        return 'optimisationIntrosTable';
      case OptimisationTestTypeEnum.FINALISATION:
      case OptimisationTestTypeEnum.TITLE:
        return 'optimisationFinalisationTable';
    }
  };

  const { gaNetworkType, setGaNetworkType } = useContext(GaNetworkTypeContext);

  const statsKey =
    gaNetworkType === GaNetworkType.SEARCH ? 'searchStats' : 'watchStats';

  const [selectedBatchs, setSelectedBatches] = useState<number[]>([]);

  const [areThumbsEnlarged, setAreThumbsEnlarged] = useState(false);

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews[optionsDataKey()],
  );

  const availableBatches = tests?.reduce(
    (previousValue: number[], currentValue) => {
      return !previousValue.includes(currentValue.batch)
        ? [...previousValue, currentValue.batch]
        : previousValue;
    },
    [],
  );

  const [sort, setSort] = useState<TestsSortState>({
    direction: SortDirection.DEFAULT,
    property: 'maxCpv',
    type: 'properties',
  });

  const winningTestId = useMemo(() => {
    const viewsThreshold = type === OptimisationTestTypeEnum.THUMBS ? 100 : 500;

    const compatibleTests = tests?.filter((test) => {
      return (test[statsKey]?.views ?? 0) >= viewsThreshold;
    });

    return maxBy(compatibleTests, (test) => test?.[statsKey]?.[testParameter])
      ?.id;
  }, [tests, testParameter, type, statsKey]);

  const sortedTests = useMemo(() => {
    const filterTests = (tests?: OptimisationTestUnitResource[]) => {
      if (!selectedBatchs.length)
        return tests?.filter((test) => test.batch !== 0);
      return tests?.filter((test) => selectedBatchs.includes(test.batch));
    };

    if (sort.direction === SortDirection.DEFAULT) {
      return filterTests(tests);
    }

    const sortType = sort.type === 'properties' ? 'properties' : statsKey;

    const property =
      sort.property as keyof OptimisationTestUnitResource[typeof sortType];

    const direction = sort.direction.toLowerCase() as 'asc' | 'desc';

    return filterTests(
      orderBy(
        tests,
        (test) => {
          if ((test[statsKey]?.views ?? 0) < 50) {
            if (sort.direction === SortDirection.DESC) {
              return -1_000_000_000;
            }
            return 1_000_000_000;
          }
          return sort.type === 'attributes'
            ? parseFloat(test[property])
            : parseFloat(test[sortType]?.[property] ?? '') || -1000;
        },
        direction,
      ),
    );
  }, [sort, tests, selectedBatchs, statsKey]);

  const renderGaNetworkTypeLabel = () => {
    switch (gaNetworkType) {
      case GaNetworkType.SEARCH:
        return 'Youtube Search';
      default:
        return 'Youtube Video';
    }
  };

  return (
    <>
      <ControlsHolder>
        <ResultsDescription>
          <ResultsDescriptionSvg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="8" />
            <path d="M7.33337 10.1568V11.3333H8.66671V10.1568H7.33337ZM7.33337 4.66659V9.37247H8.66671V4.66659H7.33337Z" />
          </ResultsDescriptionSvg>
          <Text $type="smallHeadline">
            <Dot />
            {renderGaNetworkTypeLabel()}
          </Text>
        </ResultsDescription>
        <GaNetworkSwitch>
          <GaNetworkButton
            as="button"
            type="button"
            $type="smallHeadline"
            $active={gaNetworkType === GaNetworkType.WATCH}
            onClick={() => setGaNetworkType!(GaNetworkType.WATCH)}
          >
            YTv
          </GaNetworkButton>
          <GaNetworkButton
            as="button"
            type="button"
            $type="smallHeadline"
            $active={gaNetworkType === GaNetworkType.SEARCH}
            onClick={() => setGaNetworkType!(GaNetworkType.SEARCH)}
          >
            YTs
          </GaNetworkButton>
        </GaNetworkSwitch>
        <StyledViewOptionsModal
          columns={
            type === OptimisationTestTypeEnum.SEQUENCES
              ? sequencesColumns
              : columns
          }
          tableKey={optionsDataKey()}
        />
        <FullScreenButton />
      </ControlsHolder>
      <TestsBatchFilter
        selectedBatchs={selectedBatchs}
        setSelectedBatches={setSelectedBatches}
        availableBatches={availableBatches}
        tests={tests}
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <SortableTh
                title="Name"
                sort={sort}
                setSort={setSort}
                property="name"
                type="attributes"
              />
              <SortableTh
                title="Views"
                sort={sort}
                setSort={setSort}
                property="views"
              />
              {tableViewOptions['Video Title'] && <Th>Video Title</Th>}
              {tableViewOptions['Video Duration'] && <Th>Duration</Th>}
              {tableViewOptions['CTR'] && (
                <SortableTh
                  title="CTR"
                  sort={sort}
                  setSort={setSort}
                  property="ctrPercentage"
                />
              )}
              {tableViewOptions['Watchtime (s)'] && (
                <SortableTh
                  title="Watchtime (s)"
                  sort={sort}
                  setSort={setSort}
                  property="avgWatchtime"
                />
              )}
              {tableViewOptions['e30s Reach'] && (
                <SortableTh
                  title="e30s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e30s"
                />
              )}
              {tableViewOptions['e45s Reach'] && (
                <SortableTh
                  title="e45s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e45s"
                />
              )}
              {tableViewOptions['e60s Reach'] && (
                <SortableTh
                  title="e60s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e60s"
                />
              )}
              {tableViewOptions['e80s Reach'] && (
                <SortableTh
                  title="e80s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e80s"
                />
              )}
              {tableViewOptions['e120s Reach'] && (
                <SortableTh
                  title="e120s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e120s"
                />
              )}
              {tableViewOptions['e150s Reach'] && (
                <SortableTh
                  title="e150s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e150s"
                />
              )}
              {tableViewOptions['e4min Reach'] && (
                <SortableTh
                  title="e4min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e240s"
                />
              )}
              {tableViewOptions['e8min Reach'] && (
                <SortableTh
                  title="e8min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="eMilestoneViewsPercentage"
                />
              )}
              {tableViewOptions['e15min Reach'] && (
                <SortableTh
                  title="e15min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e15m"
                />
              )}
              {tableViewOptions['e20min Reach'] && (
                <SortableTh
                  title="e20min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e20m"
                />
              )}
              {tableViewOptions['e30min Reach'] && (
                <SortableTh
                  title="e30min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e30m"
                />
              )}
              {tableViewOptions['e60min Reach'] && (
                <SortableTh
                  title="e60min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e60m"
                />
              )}
              {tableViewOptions['30s Reach'] && (
                <SortableTh
                  title="30s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="30sViewsPercentage"
                />
              )}
              {tableViewOptions['8min Reach'] && (
                <SortableTh
                  title={`8min Reach`}
                  sort={sort}
                  setSort={setSort}
                  property="milestoneViewsPercentage"
                />
              )}
              {tableViewOptions['25% Reach'] && (
                <SortableTh
                  title="25% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p25Rate"
                />
              )}
              {tableViewOptions['50% Reach'] && (
                <SortableTh
                  title="50% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p50Rate"
                />
              )}
              {tableViewOptions['75% Reach'] && (
                <SortableTh
                  title="75% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p75Rate"
                />
              )}
              {tableViewOptions['100% Reach'] && (
                <SortableTh
                  title="100% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p100Rate"
                />
              )}
              {tableViewOptions['Final Value'] && (
                <OptimisationFinalValueTh sort={sort} setSort={setSort} />
              )}
              {tableViewOptions['eYTV'] && (
                <SortableTh
                  title="eYTV"
                  sort={sort}
                  setSort={setSort}
                  property="estimatedFinalValue"
                />
              )}
              {tableViewOptions['eYTV 30s'] && (
                <SortableTh
                  title="eYTV 30s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv30s"
                />
              )}
              {tableViewOptions['eYTV 45s'] && (
                <SortableTh
                  title="eYTV 45s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv45s"
                />
              )}
              {tableViewOptions['eYTV 60s'] && (
                <SortableTh
                  title="eYTV 60s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv60s"
                />
              )}
              {tableViewOptions['eYTV 80s'] && (
                <SortableTh
                  title="eYTV 80s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv80s"
                />
              )}
              {tableViewOptions['eYTV 120s'] && (
                <SortableTh
                  title="eYTV 120s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv120s"
                />
              )}
              {tableViewOptions['eYTV 150s'] && (
                <SortableTh
                  title="eYTV 150s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv150s"
                />
              )}
              {tableViewOptions['eYTV 4min'] && (
                <SortableTh
                  title="eYTV 4min"
                  sort={sort}
                  setSort={setSort}
                  property="eytv4"
                />
              )}
              {tableViewOptions['eYTV 15min'] && (
                <SortableTh
                  title="eYTV 15min"
                  sort={sort}
                  setSort={setSort}
                  property="eytv15m"
                />
              )}
              {tableViewOptions['eYTV 20min'] && (
                <SortableTh
                  title="eYTV 20min"
                  sort={sort}
                  setSort={setSort}
                  property="eytv20m"
                />
              )}
              {tableViewOptions['eYTV 30min'] && (
                <SortableTh
                  title="eYTV 30min"
                  sort={sort}
                  setSort={setSort}
                  property="eytv30m"
                />
              )}
              {tableViewOptions['eYTV 60min'] && (
                <SortableTh
                  title="eYTV 60min"
                  sort={sort}
                  setSort={setSort}
                  property="eytv60m"
                />
              )}
              {tableViewOptions['Channel'] && <Th>Channel</Th>}
              {tableViewOptions['Audience Channels'] && (
                <Th>Audience channels</Th>
              )}
              {tableViewOptions['Audience Segment'] && (
                <Th>Audience segment</Th>
              )}
              {tableViewOptions['GA Campaign Status'] && (
                <Th>GA Campaign Status</Th>
              )}
              {tableViewOptions['Max CPV'] && (
                <SortableTh
                  title={`Max CPV`}
                  sort={sort}
                  setSort={setSort}
                  property="maxCpv"
                  type="properties"
                />
              )}
              {tableViewOptions['Campaign Budget'] && (
                <SortableTh
                  title={`Campaign budget`}
                  sort={sort}
                  setSort={setSort}
                  property="campaignBudget"
                  type="properties"
                />
              )}
              {type === OptimisationTestTypeEnum.SEQUENCES && (
                <>
                  {tableViewOptions['Drop Visualization'] && (
                    <Th>Drop Visualization</Th>
                  )}
                  {tableViewOptions['Drop'] && (
                    <Th>
                      Drop
                      <br />
                      <Text as="span" $color="gray" $type="extraSmall">
                        Period: Drop / (s)
                      </Text>
                    </Th>
                  )}
                </>
              )}
              <Th>
                <ActionsWrap>
                  <SrOnly>Delete</SrOnly>
                </ActionsWrap>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              hasData={!!sortedTests?.length}
              isLoading={false}
              isError={false}
              emptyTableText="No tests added"
            >
              {sortedTests?.map((test) => (
                <TestTableRow
                  key={test.id}
                  test={test}
                  winning={winningTestId === test.id}
                  areThumbsEnlarged={areThumbsEnlarged}
                  setAreThumbsEnlarged={setAreThumbsEnlarged}
                />
              ))}
              {sortedTests?.length && (
                <AvgStatsRow
                  tests={sortedTests}
                  tableViewOptionsKey={optionsDataKey()}
                  statsKey={statsKey}
                />
              )}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
    </>
  );
});

export default TestTable;
